import { getPageCommonProperties } from './page-context'

/**
 * The shared attribution/context properties, keyed the way Avo's generated
 * clients name their function parameters.
 *
 * `@susu/segment-common` is and stays the only reader of the underlying
 * cookies, URL params and click-ID storage — this is a pure rename of its
 * output. The snake_case it returns is the wire format (Avo sends
 * `ga_client_id`, `page_type`, … to Segment regardless); the camelCase here
 * only exists because Avo's codegen names its TypeScript parameters that way.
 *
 * Every app used to hand-roll this rename and spread the result into each
 * individual event call, which drifted: different apps forwarded different
 * subsets, and the click-ID fields were dropped everywhere. Building it once
 * here and handing it to `setSystemProperties` replaces all of those copies.
 *
 * Deliberately absent: `gaClientId`/`gaSessionId`/`gaSessionNumber`. Those
 * have their own reader in `initialize.ts` (`currentGAIdentity`) with
 * bounded-wait and omit-when-unwritten semantics that segment-common's plain
 * cookie read lacks. `platform`/`lastPlatform` are archived in the tracking
 * plan and are not sent at all.
 */
export const currentCommonSystemProperties = (): Record<string, string> => {
  const common = getPageCommonProperties()

  // An empty value must not be sent. Several of these are closed enums in the
  // tracking plan (country, language, pageType, loggedIn), so '' is an invalid
  // value rather than a neutral one — and the generated setSystemProperties
  // skips a key that is absent, leaving whatever was already set.
  return withoutEmptyValues({
    country: common.country,
    language: common.language,
    loggedIn: common.logged_in,
    userId: common.user_id,
    pageType: common.page_type,
    fbFbp: common.fb_fbp,
    fbFbc: common.fb_fbc,
    gaAwClid: common.ga_aw_clid,
    affiliateMarker: common.affiliateMarker,
    awc: common.awc,
    ttclid: common.ttclid,
    msclkid: common.msclkid,
    twclid: common.twclid,
    rdtCid: common.rdt_cid,
    epik: common.epik,
  })
}

const withoutEmptyValues = (
  properties: Record<string, string | undefined>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(properties).filter(([, value]) => Boolean(value)),
  ) as Record<string, string>
